/**
 * useMjAction — MJ Action 操作 Hook
 *
 * 职责：
 *   1. 在源节点右侧创建 loading 新节点 + 连接边
 *   2. POST /api/mj-action 发起后端异步任务
 *   3. 等待 WebSocket node-updated 推送结果
 *   4. handleMjInpaint: 打开遮罩编辑器 → 提交 /api/mj-inpaint
 */
import { useCallback, useState } from 'react';
import { useReactFlow } from 'reactflow';
import * as api from '../api';
import * as C from '../constants';

const ACTION_LABELS = {
  upscale: '放大',
  variation: '变体',
  reroll: '重做',
  subtle_upscale: '细腻放大',
  creative_upscale: '创意放大',
  zoom: '缩放',
  pan: '平移',
  high_variation: '强变体',
  low_variation: '弱变体',
  inpaint: '局部重绘',
};

// 决定新节点的 mjPhase（与后端保持一致）
function resolvePhase(actionType) {
  // zoom/pan 返回四宫格，需要 grid 阶段来放大选图
  const gridActions = ['variation', 'reroll', 'high_variation', 'low_variation', 'zoom', 'pan'];
  return gridActions.includes(actionType) ? 'grid' : 'upscaled';
}

export default function useMjAction({ setNodes, setEdges, projectId, setAlertMsg, callbacksRef }) {
  const { getNodes } = useReactFlow();

  // ── Inpaint 遮罩编辑器状态 ──
  const [inpaintState, setInpaintState] = useState(null); // { sourceNodeId, imageUrl }

  const handleMjAction = useCallback(async (sourceNodeId, actionType, params = {}) => {
    // 🖌️ inpaint 特殊处理：打开遮罩编辑器而不是直接提交
    if (actionType === 'inpaint') {
      const sourceNode = getNodes().find(n => n.id === sourceNodeId);
      if (!sourceNode) {
        setAlertMsg?.({ type: 'error', text: '源节点不存在' });
        return;
      }
      const imageUrl = sourceNode.data?.localCacheUrl || sourceNode.data?.output;
      if (!imageUrl) {
        setAlertMsg?.({ type: 'error', text: '源节点没有图片' });
        return;
      }
      setInpaintState({ sourceNodeId, imageUrl });
      return;
    }
    const sourceNode = getNodes().find(n => n.id === sourceNodeId);
    if (!sourceNode) {
      setAlertMsg?.({ type: 'error', text: '源节点不存在' });
      return;
    }

    const actionLabel = ACTION_LABELS[actionType] || actionType;
    const targetId = `node-${Date.now()}-mj`;
    const newPhase = resolvePhase(actionType);

    // 计算新节点位置（源节点右侧偏移）
    const sourcePos = sourceNode.position;
    const sourceWidth = sourceNode.data?.customSize?.width || 240;
    const newPos = { x: sourcePos.x + sourceWidth + 60, y: sourcePos.y };

    // 创建 loading 新节点
    const newNode = {
      id: targetId,
      type: 'aiNode',
      position: newPos,
      selected: false,
      data: {
        type: 'image',
        // 标题仅显示“当前动作”，不再串联历史动作链（如 变体_放大_局部重绘）。
        title: actionLabel,
        loading: true,
        generationStartTime: Date.now(),
        generationModelName: 'Midjourney',
        generationModelLogo: sourceNode.data?.modelLogo || sourceNode.data?.generationModelLogo,
        statusInfo: { status: `${actionLabel} 处理中...` },
        // 继承源节点的渠道，确保 Action 路由到正确的 MJ 供应商（云雾 / APImart）
        vendor: sourceNode.data?.vendor || 'yunwu-midjourney',
        mjPhase: newPhase,
        mjSourceNodeId: sourceNodeId,
        // 保存动作快照：失败重试时由 useNodeActions 重放 /api/mj-action。
        mjActionType: actionType,
        mjActionParams: {
          index: params.index || undefined,
          zoomLevel: params.zoomLevel || undefined,
          panDirection: params.panDirection || undefined,
        },
        isDetached: true,
        isDirectUpload: true,
        customSize: sourceNode.data?.customSize || { width: 240, height: 240 },
        // 注入回调（将由 EditorPage rebind 覆盖，这里先给初始值防止空指针）
        onUpdate: callbacksRef?.current?.onUpdate,
        onDelete: callbacksRef?.current?.onDelete,
        onGenerate: callbacksRef?.current?.onGenerate,
        onReupload: callbacksRef?.current?.onReupload,
        onReplace: callbacksRef?.current?.onReplace,
        onDetach: callbacksRef?.current?.onDetach,
        onRemoveRefFile: callbacksRef?.current?.onRemoveRefFile,
        onRemoveRefText: callbacksRef?.current?.onRemoveRefText,
        onRemoveRefAudio: callbacksRef?.current?.onRemoveRefAudio,
        onHandleDoubleClick: callbacksRef?.current?.onHandleDoubleClick,
        onDescribe: callbacksRef?.current?.onDescribe,
        onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
        getMjDescribeConfig: callbacksRef?.current?.getMjDescribeConfig,
        globalAssetsRef: callbacksRef?.current?.globalAssetsRef,
        publicAssetsRef: callbacksRef?.current?.publicAssetsRef,
      }
    };

    // 连接边（锁定，表示推导关系）
    const newEdge = {
      id: `edge-mj-${sourceNodeId}-${targetId}`,
      source: sourceNodeId,
      target: targetId,
      type: 'custom',
      data: { locked: true, originalColor: C.EDGE_COLOR_AUTO, isTransmitting: true },
      style: { stroke: C.EDGE_COLOR_AUTO, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE },
    };

    // 添加节点和边
    setNodes(nds => [...nds, newNode]);
    setEdges(eds => [...eds, newEdge]);

    // index 继承逻辑：
    // - grid 阶段（U1-U4/V1-V4）：params.index 是用户点的格子号，直接用
    // - upscaled 阶段（强/弱变体等，前端不传 index）：APImart 的 variation index
    //   指「从父四宫格选第几张」，必须沿用该单图放大时的原始 index，
    //   否则会默认 index=1 → 作用到四宫格第一张而非当前放大图。
    const inheritedIndex = params.index
      || sourceNode.data?.mjActionParams?.index
      || sourceNode.data?.mjIndex
      || undefined;

    // 发送 API 请求
    try {
      await api.apiPost('/api/mj-action', {
        projectId,
        sourceNodeId,
        targetNodeId: targetId,
        actionType,
        index: inheritedIndex,
        zoomLevel: params.zoomLevel || undefined,
        panDirection: params.panDirection || undefined,
        // 带上源节点关键字段，避免新节点尚未落库导致后端查不到（竞态）
        sourceMeta: {
          vendorTaskId: sourceNode.data?.vendorTaskId || null,
          vendor: sourceNode.data?.vendor || null,
          output: sourceNode.data?.output || null,
          mjButtons: sourceNode.data?.mjButtons || [],
          mjIndex: sourceNode.data?.mjActionParams?.index || sourceNode.data?.mjIndex || null,
        },
      });
    } catch (err) {
      console.error('[useMjAction] API Error:', err);
      // API 调用失败时直接更新节点状态
      setNodes(nds => nds.map(n => {
        if (n.id === targetId) {
          return { ...n, data: { ...n.data, loading: false, error: err.message || 'MJ Action 请求失败' } };
        }
        return n;
      }));
      setAlertMsg?.({ type: 'error', text: `MJ ${actionLabel} 失败: ${err.message}` });
    }
  }, [getNodes, setNodes, setEdges, projectId, setAlertMsg, callbacksRef]);

  // ── Inpaint 提交回调（由 InpaintMaskEditor 调用）──
  const handleMjInpaintSubmit = useCallback(async ({ maskBase64, prompt, maskMeta }) => {
    if (!inpaintState) return;
    const { sourceNodeId } = inpaintState;

    const allNodes = getNodes();
    const sourceNode = allNodes.find(n => n.id === sourceNodeId);
    if (!sourceNode) {
      setAlertMsg?.({ type: 'error', text: '源节点不存在(前端提交时)' });
      return;
    }

    const targetId = `node-${Date.now()}-mj-inpaint`;
    const sourcePos = sourceNode.position;
    const sourceWidth = sourceNode.data?.customSize?.width || 240;

    const newNode = {
      id: targetId,
      type: 'aiNode',
      position: { x: sourcePos.x + sourceWidth + 60, y: sourcePos.y },
      selected: false,
      data: {
        type: 'image',
        // 标题仅显示当前动作，避免继承上游标题导致链式变长。
        title: '局部重绘',
        loading: true,
        generationStartTime: Date.now(),
        generationModelName: 'Midjourney',
        generationModelLogo: sourceNode.data?.modelLogo || sourceNode.data?.generationModelLogo,
        statusInfo: { status: '局部重绘处理中...' },
        // 继承源节点的渠道，确保 inpaint 路由到正确的 MJ 供应商
        vendor: sourceNode.data?.vendor || 'yunwu-midjourney',
        mjPhase: 'upscaled',
        mjSourceNodeId: sourceNodeId,
        mjActionType: 'inpaint',
        isDetached: true,
        isDirectUpload: true,
        customSize: sourceNode.data?.customSize || { width: 240, height: 240 },
        onUpdate: callbacksRef?.current?.onUpdate,
        onDelete: callbacksRef?.current?.onDelete,
        onGenerate: callbacksRef?.current?.onGenerate,
        onReupload: callbacksRef?.current?.onReupload,
        onReplace: callbacksRef?.current?.onReplace,
        onDetach: callbacksRef?.current?.onDetach,
        onRemoveRefFile: callbacksRef?.current?.onRemoveRefFile,
        onRemoveRefText: callbacksRef?.current?.onRemoveRefText,
        onRemoveRefAudio: callbacksRef?.current?.onRemoveRefAudio,
        onHandleDoubleClick: callbacksRef?.current?.onHandleDoubleClick,
        onDescribe: callbacksRef?.current?.onDescribe,
        onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
        getMjDescribeConfig: callbacksRef?.current?.getMjDescribeConfig,
        globalAssetsRef: callbacksRef?.current?.globalAssetsRef,
        publicAssetsRef: callbacksRef?.current?.publicAssetsRef,
      }
    };

    const newEdge = {
      id: `edge-mj-${sourceNodeId}-${targetId}`,
      source: sourceNodeId,
      target: targetId,
      type: 'custom',
      data: { locked: true, originalColor: C.EDGE_COLOR_AUTO, isTransmitting: true },
      style: { stroke: C.EDGE_COLOR_AUTO, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE },
    };

    setNodes(nds => [...nds, newNode]);
    setEdges(eds => [...eds, newEdge]);

    try {
      await api.apiPost('/api/mj-inpaint', {
        projectId,
        sourceNodeId,
        targetNodeId: targetId,
        maskBase64,
        prompt: prompt || undefined,
        maskMeta: maskMeta || undefined,
        // 直接带上源节点关键字段，避免新放大节点尚未落库导致后端查不到（竞态）
        sourceMeta: {
          vendorTaskId: sourceNode.data?.vendorTaskId || null,
          vendor: sourceNode.data?.vendor || null,
          output: sourceNode.data?.output || null,
          mjButtons: sourceNode.data?.mjButtons || [],
        },
      });
    } catch (err) {
      setNodes(nds => nds.map(n => {
        if (n.id === targetId) {
          return { ...n, data: { ...n.data, loading: false, error: err.message || 'MJ 局部重绘请求失败' } };
        }
        return n;
      }));
      setAlertMsg?.({ type: 'error', text: `MJ 局部重绘失败: ${err.message}` });
    }
  }, [inpaintState, getNodes, setNodes, setEdges, projectId, setAlertMsg, callbacksRef]);

  const closeInpaintEditor = useCallback(() => setInpaintState(null), []);

  return { handleMjAction, inpaintState, handleMjInpaintSubmit, closeInpaintEditor };
}
